import React, { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import { XMasonry, XBlock } from "react-xmasonry";
import { useInView } from "react-intersection-observer";
import { UserAuth } from "../Context";

import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../FirebaseConfig";
import useLogsStore from "../StatesStore/Logs";
import useUserDataStore from "../StatesStore/UserData";
import Skeleton from "../Products/Skeleton";

import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import ProductCard8 from "../Products/ProductCard8";
import { useErrorBoundary } from "react-error-boundary";
import GoogleAdCardMobile from "../NewsFeed/GoogleAdCardMobile";
import GoogleAdCard4 from "../NewsFeed/GoogleAdCard4";
import useRestoreScrollPositionStore from "../StatesStore/RestoreScrollPosition";

export default function Discover2() {
  const { showBoundary } = useErrorBoundary();
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { ref: loadMoreRef, inView: loadMoreView } = useInView({
    threshold: 0.1,
  });

  const { user } = UserAuth();
  const logs = useLogsStore((state) => state.logs);
  const userData = useUserDataStore((state) => state.userData);

  const [productIds, setProductIds] = useState(null);
  const { setScrollPosition, getPosition } = useRestoreScrollPositionStore();
  const location = useLocation();
  const currentUrl = location.pathname + location.search;
  const scrollContainerRef = useRef(null);
  const isScrollRestored = useRef(false);
  // Fetch product IDs from logs or user preferences
  const fetchProductIdsFromLogs = async () => {
    if (logs && logs.length > 0) return logs;
    const ids = [];
    try {
      const logsRef = collection(db, "users", user?.uid, "logs");
      const logsQuery = query(
        logsRef,
        where("productId", "!=", null),
        orderBy("timestamp", "desc"),
        limit(10)
      );
      const logsSnapshot = await getDocs(logsQuery);

      logsSnapshot.forEach((doc) => {
        const logData = doc.data();
        if (logData.productId) ids.push(logData.productId);
      });

      return ids;
    } catch (error) {
      console.error("Error fetching productIds from logs:", error);
      return [];
    }
  };

  const {
    data,
    error,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ["products"],
    queryFn: async ({ pageParam = 1 }) => {
      try {
        const response = await fetch(`${process.env.REACT_APP_RECOMMENDER}/api/recommend/${user?.uid}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            product_ids: productIds || [],
            page_number: pageParam,
            items_per_page: 25,
            favCategories: userData?.favCategories || [],
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data;
      } catch (error) {
        showBoundary(error);
        throw error; 
      }
    },
    enabled: productIds !== null,
    staleTime: Infinity,
    cacheTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchInterval: false,
    placeholderData: (previousData) => {
      // Return previous data if available, otherwise return skeleton placeholders
      if (previousData) return previousData;

      return {
        pages: [
          Array.from({ length: 25 }, (_, index) => ({
            id: `skeleton-${index}`, // Unique ID for each skeleton
            isSkeleton: true, // Flag to identify this as a skeleton placeholder
            name: "Loading...", // Placeholder text
          })),
        ],
        pageParams: [1],
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      // If the last page is shorter than the page size, assume it's the last
      return lastPage.length < 15 ? undefined : allPages.length + 1;
    },
  });
  // Load product IDs on component mount
  useEffect(() => {
    const initializeProductIds = async () => {
      if (!user) return;

      const ids = await fetchProductIdsFromLogs();
      setProductIds(ids);
    };

    initializeProductIds();
  }, [user]);

  // Fetch products whenever page number changes
  // useEffect(() => {
  //   if (productIds) {
  //     fetchProducts();
  //   }
  // }, [productIds, pageNumber]);

  // Trigger next page fetch when the "load more" ref is in view
  // useEffect(() => {
  //   if (loadMoreView && hasNextPage && !isLoading) {
  //     setPageNumber((prev) => prev + 1);
  //   }
  // }, [loadMoreView]);

  useEffect(() => {
    if (loadMoreView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [loadMoreView]);

  // Handle scroll position saving
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setScrollPosition(currentUrl, scrollTop);
    };

    // Throttle scroll events for better performance
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledScroll);
    return () => window.removeEventListener('scroll', throttledScroll);
  }, [currentUrl]);

  // Restore scroll position when data is loaded
  useEffect(() => {
    if (data && !isScrollRestored.current && !isLoading) {
      const savedPosition = getPosition(currentUrl);
      console.log('savedPosition',savedPosition);
      
      if (savedPosition > 0) {
        // Use setTimeout to ensure DOM is fully rendered
        setTimeout(() => {
          window.scrollTo({
            top: savedPosition,
            behavior: 'instant' // Use 'instant' to avoid smooth scrolling
          });
          isScrollRestored.current = true;
        });
      } else {
        isScrollRestored.current = true;
      }
    }
  }, [data]);

  // Reset scroll restoration flag when URL changes
  useEffect(() => {
    isScrollRestored.current = false;
  }, [currentUrl]);
  function getTargetBlockWidth() {
    const screenWidth = window.innerWidth;
    return screenWidth < 1400 ? 200 : 230;
  }
  // function getTargetBlockWidth() {
  //   const screenWidth = window.innerWidth;
  //   return screenWidth < 440 ? 200 : 220;
  // }
  const isMobileScreen = window.innerWidth <= 768;
  return (
    <>
      {/* <SideNavUserProfile /> */}
      {/* <div className="col-md-8 col-lg-6 vstack gap-4 p-0 mt-0 mt-lg-5 px-3 px-lg-0 me-2"> */}
      <div
        ref={scrollContainerRef}
        className="vstack px-lg-0"
        // className="vstack "
        style={{
          padding: isSmallScreen ? "8px 4.5px" : "20px 4.5px",
          gap: "12px",
        }}
      >
        <div
          style={{
            padding: "0px 4px",
            fontFamily: "Inter",
            fontWeight: isSmallScreen ? "500" : "600",
            fontSize: "20px",
            lineHeight: "100%",
            letterSpacing: "0%",
            color: "#000000",
          }}
        >
          Recommended for you
        </div>
        <XMasonry
          center={true}
          responsive={true}
          maxColumns={5}
          targetBlockWidth={getTargetBlockWidth()}
        >
          {/* <XBlock>{ <GoogleAdCard2 />}</XBlock> */}
          {data?.pages?.flat()?.map((product, index) => {
            // if (!user && index === 40) {
            //   return (
            //     <XBlock key="login-ref">
            //       <div ref={loginRef}></div>
            //     </XBlock>
            //   );
            // }
            // if (index === 0) {
            //   return (
            //     <XBlock key="abc">
            //       <Skeleton />
            //     </XBlock>
            //   );
            // }
            if ((index + 1) % 9 === 0) {
              return (
                <XBlock key={`ad-${index}`} width={isMobileScreen ? 2 : 1}>
                 {isMobileScreen ? <GoogleAdCardMobile /> : <GoogleAdCard4 uniqueKey={`desktop-ad-${index}`} />}
                </XBlock>

              );
            }
            // Default product card rendering
            const isSecondLast = index === data?.pages?.flat().length - 12;
            return (
              <XBlock key={product.id + index} width={1}>
                {product.isSkeleton ? (
                  <Skeleton /> // Render the Skeleton component for placeholders
                ) : (
                  <div ref={isSecondLast ? loadMoreRef : null}>
                    <ProductCard8 product={product} index={index} />
                  </div>
                )}
              </XBlock>
            );
          })}
          {isFetchingNextPage &&
            Array.from({ length: 25 }).map((_, index) => (
              <XBlock key={`skeleton-${index}`} width={1}>
                <Skeleton />
              </XBlock>
            ))}
          {/* <XBlock>
            <div ref={loadMoreRef}></div>
          </XBlock> */}
        </XMasonry>

        {isLoading && (
          <div className="d-flex justify-content-center align-items-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
